import { useMarketplaceContext } from 'src/contexts/marketplace';
import { useAuth } from 'src/utils/auth';
import {
  Box,
  Heading,
  Text,
  Button,
  useToast,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  useDisclosure,
  Container,
  Flex,
  SimpleGrid,
  Spinner,
  Card,
  CardBody,
  CardHeader,
  HStack,
  VStack,
  ListItem,
  UnorderedList,
  Skeleton,
  useTheme,
} from '@chakra-ui/react';
import { FiCheck } from 'react-icons/fi';
import { useCallback, useRef, useState, useMemo } from 'react';
import { useRouter } from 'next/router';
import useRequest from 'src/utils/hooks/useRequest';
import { useDeepEffect } from 'src/utils/hooks';
import CheckoutModal from 'src/components/molecules/checkout-modal';
import { CONTAINER_MAX_WIDTH_1 } from 'src/constants/ui';

export const FREE_TIER_POSITION = 1;
const CURATOR_TIER_POSITION = 2;
const COMMUNITY_TIER_POSITION = 3;

interface TierInfo {
  name: string;
  price: number;
  position: number;
  description: string;
  features: { icon?: React.ReactNode; text: string }[];
  details: React.ReactNode;
}

const TierOptionsPage = (): JSX.Element => {
  const { marketplace, urlBase } = useMarketplaceContext();
  const { hasAdmin, isAuthenticated, authLoading, userLoaded } = useAuth();
  // Only evaluate admin status after the user object has been loaded to avoid a false negative
  const isAdmin = userLoaded ? hasAdmin(marketplace?.id) : false;
  const router = useRouter();
  const currentTierPosition = marketplace?.tier?.position ?? 0;
  const toast = useToast();
  const { api } = useRequest();
  const [selectedTier, setSelectedTier] = useState<TierInfo | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [sessionKey, setSessionKey] = useState<number>(0);
  const [isCancelling, setIsCancelling] = useState<boolean>(false);
  const { isOpen, onClose, onToggle } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);

  // Tier information with descriptions and key features
  const tierInfo = {
    free: {
      name: 'Free',
      price: 0,
      position: FREE_TIER_POSITION,
      description: 'Create a commerce platform for your audience',
      features: [
        {
          text: 'Single Category',
        },
        {
          text: 'Customized UI for your audience',
        },
        {
          icon: (
            <Box display="flex" alignItems="center">
              📣
            </Box>
          ),
          text: 'Manually promote your platform to your audience or community',
        },
        {
          icon: (
            <Box display="flex" alignItems="center">
              🚫
            </Box>
          ),
          text: 'No ability to collect user fees or commissions',
        },
      ],
      details: (
        <UnorderedList className="list-disc list-inside" fontSize="sm" textAlign="left">
          <ListItem>Custom Filter Attributes</ListItem>
          <ListItem>Configurable UI</ListItem>
          <ListItem>Basic Emails to Users</ListItem>
          <ListItem>No User Payment (Free Classifieds or Free Directory)</ListItem>
          <ListItem>{`${marketplace?.slug}.withloma.com`}</ListItem>
        </UnorderedList>
      ),
    },
    curator: {
      name: 'Curator',
      price: 9,
      position: CURATOR_TIER_POSITION,
      description: 'Generate some revenue and automate promotions',
      features: [
        {
          text: 'Built-in social marketing automation',
        },
        {
          text: 'Use AI to launch with real listings on Day 1',
        },
        {
          text: 'Charge fees for transactions, listings, or visibility',
        },
      ],
      details: (
        <UnorderedList fontSize="sm" textAlign="left">
          <ListItem>Up to 3 Categories</ListItem>
          <ListItem>Custom Filter Attributes</ListItem>
          <ListItem>Configurable UI</ListItem>
          <ListItem>Basic Emails to Users</ListItem>
          <ListItem>
            User Payment Enabled (Collect Fees from Marketplace, Classifieds, or Directory)
          </ListItem>
          <ListItem>{`${marketplace?.slug}.withloma.com`}</ListItem>
          <ListItem>
            Built-In Social Marketing Automation (Connect Directly to Your Social Accounts and
            Customize Promotions)
          </ListItem>
          <ListItem>1x AI-Powered Auto-Population</ListItem>
          <ListItem>One-at-a-Time Listing Import Tool</ListItem>
        </UnorderedList>
      ),
    },
    community: {
      name: 'Community',
      price: 49,
      position: COMMUNITY_TIER_POSITION,
      description: 'Turn your platform into a standalone business',
      features: [
        {
          text: 'Custom domain integration',
        },
        {
          text: 'Unlimited AI-powered automation',
        },
        {
          text: 'Advanced monetization options',
        },
      ],
      details: (
        <UnorderedList fontSize="sm" textAlign="left">
          <ListItem>Unlimited Categories</ListItem>
          <ListItem>Custom Filter Attributes</ListItem>
          <ListItem>Configurable UI</ListItem>
          <ListItem>Advanced Custom Emails to Users</ListItem>
          <ListItem>
            User Payment Enabled (Collect Fees from Marketplace, Classifieds, or Directory)
          </ListItem>
          <ListItem>Custom Domain Integration</ListItem>
          <ListItem>
            Built-In Social Marketing Automation (Connect Directly to Your Social Accounts and
            Customize Promotions)
          </ListItem>
          <ListItem>Daily AI-Powered Auto-Population</ListItem>
          <ListItem>Bulk Listing Import Tool</ListItem>
          <ListItem>Connect Payouts to Your Stripe Account</ListItem>
        </UnorderedList>
      ),
    },
  };

  // Pricing card component for simplified design
  const PricingCard = ({ tier }: { tier: TierInfo }) => {
    const [showDetails, setShowDetails] = useState(false);
    const isCommunity = tier.position === COMMUNITY_TIER_POSITION;
    const isCurator = tier.position === CURATOR_TIER_POSITION;
    const isFree = tier.position === FREE_TIER_POSITION;
    const theme = useTheme();

    const getButtonText = () => {
      if (currentTierPosition > tier.position) return 'Downgrade';
      if (currentTierPosition === tier.position) return 'Current Tier';
      return 'Upgrade';
    };

    const getButtonProps = () => {
      if (currentTierPosition > tier.position) {
        return {
          colorScheme: 'primary' as const,
          onClick: () => changeTierByPosition(tier.position),
        };
      } else if (currentTierPosition === tier.position) {
        return {
          colorScheme: 'gray' as const,
          variant: 'outline' as const,
          isDisabled: true,
        };
      }
      return {
        colorScheme: 'primary' as const,
        onClick: () => changeTierByPosition(tier.position),
      };
    };

    const isCurrentTier = currentTierPosition === tier.position;

    // Neutral visual differentiation (no loud colors)
    const cardBg = 'white';
    const shadow = isCommunity ? 'xl' : isCurator ? 'lg' : 'md';
    const topBarBg = isCommunity
      ? 'linear(to-r, gray.300, gray.200)'
      : isCurator
      ? 'linear(to-r, gray.200, gray.100)'
      : 'linear(to-r, gray.100, gray.100)';
    const headingColor = 'gray.800';
    const priceColor = 'gray.800';

    return (
      <Box h="100%" position="relative">
        {isCurrentTier && (
          <Box
            zIndex={1000}
            position="absolute"
            top="-14px"
            left="50%"
            transform="translateX(-50%)"
            bg="primary.500"
            color="white"
            px={3}
            py={1}
            borderRadius="md"
            fontSize="sm"
            fontWeight="bold"
          >
            Current Tier
          </Box>
        )}
        <Card
          variant="outline"
          position="relative"
          bg={cardBg}
          shadow={shadow}
          h="100%"
          display="flex"
          flexDirection="column"
          borderTopLeftRadius={theme.border_radius.border_radius_2}
          borderTopRightRadius={theme.border_radius.border_radius_2}
          overflow="hidden"
        >
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            borderTopRadius={theme.border_radius.border_radius_2}
            h="6px"
            bgGradient={topBarBg}
          />

          <CardHeader textAlign="center" pb={4}>
            <VStack spacing={4}>
              <Heading as="h3" size="lg" color={headingColor}>
                {tier.name}
              </Heading>

              <HStack justify="center" align="baseline">
                <Text fontSize="lg" color="gray.600">
                  $
                </Text>
                <Text fontSize="4xl" fontWeight="bold" color={priceColor}>
                  {tier.price}
                </Text>
                {tier.position !== FREE_TIER_POSITION && (
                  <Text fontSize="lg" color="gray.600">
                    /mo
                  </Text>
                )}
              </HStack>

              <Flex justify="center" align="center">
                <Text color="gray.600" textAlign="center" fontSize="md" px={2}>
                  {tier.description}
                </Text>
              </Flex>
            </VStack>
          </CardHeader>

          <CardBody pt={0} flex="1" display="flex" flexDirection="column">
            <VStack spacing={6} align="stretch" flex="1" mb={4}>
              <Flex direction="column" gap={2} flex="1" minH={0} overflowY="auto">
                {tier.features.map((feature, index) => (
                  <Flex key={index} align="start">
                    {feature.icon ? (
                      feature.icon
                    ) : (
                      <Box mt={1}>
                        <FiCheck />
                      </Box>
                    )}
                    <Text
                      fontSize="sm"
                      color="gray.800"
                      fontWeight={isFree ? 'normal' : 'medium'}
                      ml={2}
                    >
                      {feature.text}
                    </Text>
                  </Flex>
                ))}
              </Flex>

              <Flex justify="center" align="center">
                <Button
                  variant="ghost"
                  size="sm"
                  color="gray.700"
                  onClick={() => setShowDetails(!showDetails)}
                  fontWeight="normal"
                >
                  {showDetails ? 'Hide details' : 'Details...'}
                </Button>
              </Flex>

              {showDetails && <Box mb={4}>{tier.details}</Box>}
            </VStack>
            <Flex>
              {!selectedTier && (
                <Button size="lg" w="full" {...getButtonProps()}>
                  {getButtonText()}
                </Button>
              )}
            </Flex>
          </CardBody>
        </Card>
      </Box>
    );
  };

  // Helper function to get tier info by position
  const getTierByPosition = (position: number): TierInfo => {
    const tierEntries = Object.entries(tierInfo);
    const tierEntry = tierEntries.find(([, info]) => info.position === position);
    return tierEntry ? tierEntry[1] : tierInfo.free;
  };

  // Helper function to handle tier changes
  const changeTierByPosition = (position: number) => {
    const tier = getTierByPosition(position);
    changeTier(tier);
  };

  const tryCancelTier = async () => {
    setIsCancelling(true);
    try {
      await cancelTier();
      onClose();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to cancel tier subscription. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsCancelling(false);
    }
  };

  const cancelTier = async () => {
    if (!marketplace) return;
    await api.marketplace.tier.cancel(marketplace.id);
    // clear the local cache of the marketplace
    const hostname = window.location.hostname || marketplace?.domain?.domain;
    localStorage.removeItem(`marketplace-${hostname}`);
    router.push('/admin/info?success=clear');
  };

  const changeTier = (tier: TierInfo) => {
    if (tier.position === FREE_TIER_POSITION) {
      // trigger downgrade directly with confirm dialog
      onToggle();
    } else {
      const isDowngrade = currentTierPosition > tier.position;
      const href = `mailto:contact@withloma.com?subject=I want to ${
        isDowngrade ? 'downgrade' : 'upgrade'
      } to ${tier.name} Tier`;
      const anchor = document.createElement('a');
      anchor.href = href;
      anchor.click();
      // TODO: Enable once payment is integrated
      // setSelectedTier(tier);
      // setShowModal(true);
    }
  };

  const fetchClientSecret = useCallback(() => {
    if (urlBase === '' || !marketplace || selectedTier === null) {
      return Promise.resolve('');
    }

    return api.marketplace.tier.createCheckoutSession(marketplace.id, {
      tier_position: selectedTier.position,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlBase, marketplace?.id, selectedTier]);

  useDeepEffect(() => {
    const fetchCheckoutSession = async (marketplaceId: number, checkoutSessionId: string) => {
      const checkoutSession = await api.checkoutSession.get(marketplaceId, checkoutSessionId);
      if (checkoutSession.status === 'complete') {
        toast({
          title: 'Success',
          description:
            'Your payment was successfully processed! It may take up to a minute for the payment to process, try refreshing if the changes are not reflected immediately',
          status: 'success',
          duration: 9000,
          isClosable: true,
        });
      }
    };

    if (router.isReady && router.query.checkout_session_id && marketplace?.id) {
      const checkoutSessionId = router.query.checkout_session_id as string;
      fetchCheckoutSession(marketplace.id, checkoutSessionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, router.query.checkout_session_id, marketplace?.id]);

  const shouldRenderPage = useMemo(() => {
    return isAdmin && isAuthenticated;
  }, [isAdmin, isAuthenticated]);

  // Early return if marketplace/auth/feature flag are not ready — show a richer loading state
  if (!marketplace || !isAuthenticated || authLoading || !userLoaded) {
    return (
      <Box as="section" w="full">
        <Container maxW={CONTAINER_MAX_WIDTH_1} py="40px" textAlign="center">
          <Spinner size="xl" thickness="4px" speed="0.65s" color="primary.500" mr={4} />
          <Text mt={4} color="gray.600">
            Loading marketplace settings...
          </Text>

          <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={8} mt={8} maxW="1200px" mx="auto">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} height="320px" borderRadius="md" />
            ))}
          </SimpleGrid>
        </Container>
      </Box>
    );
  }

  return (
    <Box as="section" w="full">
      <Box borderBottomWidth="1px" bg="bg.surface" w="full">
        <Container maxW={CONTAINER_MAX_WIDTH_1} py="10px">
          {shouldRenderPage ? (
            <>
              {/* Stripe checkout modal */}
              {selectedTier && (
                <CheckoutModal
                  key={sessionKey}
                  isOpen={showModal}
                  onClose={() => {
                    setShowModal(false);
                    setSessionKey(sessionKey + 1);
                    setSelectedTier(null);
                  }}
                  fetchClientSecret={fetchClientSecret}
                />
              )}

              <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose}>
                <AlertDialogOverlay>
                  <AlertDialogContent>
                    <AlertDialogHeader fontSize="lg" fontWeight="bold">
                      Revert to Free Tier
                    </AlertDialogHeader>
                    <AlertDialogBody>
                      Are you sure? Your tier subscription will remain active until the end of the
                      billing cycle.
                    </AlertDialogBody>

                    <AlertDialogFooter>
                      <Button ref={cancelRef} onClick={onClose} isDisabled={isCancelling}>
                        No, Go back
                      </Button>
                      <Button
                        colorScheme="red"
                        onClick={tryCancelTier}
                        ml={3}
                        isLoading={isCancelling}
                        loadingText="Cancelling..."
                      >
                        Yes, Revert to Free Tier
                      </Button>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialogOverlay>
              </AlertDialog>

              {/* Page title */}
              <VStack spacing={8} align="stretch">
                <Box textAlign="center" mb={8}>
                  <Heading as="h1" size="xl" my={4}>
                    Choose Your Plan
                  </Heading>
                  <Text fontSize="lg" color="gray.600">
                    Select the perfect tier for your audience
                  </Text>
                </Box>

                {/* Pricing cards grid */}
                <SimpleGrid
                  columns={{ base: 1, lg: 3 }}
                  spacing={8}
                  maxW="1200px"
                  mx="auto"
                  w="full"
                  alignItems="stretch"
                >
                  <PricingCard tier={tierInfo.free} />
                  <PricingCard tier={tierInfo.curator} />
                  <PricingCard tier={tierInfo.community} />
                </SimpleGrid>
              </VStack>
              <br />
              <br />
            </>
          ) : (
            <div>Marketplace tiers are not yet available.</div>
          )}
        </Container>
      </Box>
    </Box>
  );
};

export default TierOptionsPage;
